"use client"
import { useState, useEffect } from "react"
import { Container, Box, Typography, Button, useTheme, Paper } from "@mui/material"
import { FaDiscord } from "react-icons/fa"
import PeopleIcon from "@mui/icons-material/People"
import ForumIcon from "@mui/icons-material/Forum"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import config from "../config"
// Import the ImageFanDisplay component at the top of the file
import ImageFanDisplay from "../components/ImageFanDisplay"

const Home = () => {
  const theme = useTheme()
  const DISCORD_INVITE_LINK = config.DISCORD_INVITE_LINK || "https://discord.gg/4Sy9nGasJV"

  // State for bot images
  const [botImages, setBotImages] = useState([])
  const [configImages, setConfigImages] = useState([])

  // Handle external navigation
  const handleExternalNavigation = (url) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  // Load image paths
  useEffect(() => {
    // This is a simplified approach - in a real app, you might want to use an API or import mechanism
    // to get the actual list of images from the server
    const mainImageNames = ["BotWindow_BoxCollect.png", "attends.png", "cubi-grave.png"]

    const configImageNames = [
      "AvoidRegionEditor.png",
      "BackgroundTasks_GGSpinnerFeat.png",
      "KillNCollect.png",
      "LocaleChangeTR.png",
      "Login.png",
      "ModderFeatures.png",
      "PreferredRegionEditor.png",
      "ProfileSwitcher.png",
      "ProxySupport.png",
      "Stats_Items.png",
      "Tool.png",
      "UISettings.png",
    ]

    // Create paths for the images
    const mainImages = mainImageNames.map((name) => `/bot-images/${name}`)
    const cfgImages = configImageNames.map((name) => `/bot-images/config/${name}`)

    setBotImages(mainImages)
    setConfigImages(cfgImages)
  }, [])

  return (
    <Container maxWidth="xl" sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          position: "relative",
          minHeight: "80vh",
        }}
      >
        {/* Left side images */}
        <ImageFanDisplay 
          images={[...botImages, ...configImages]}
          position="both"
          maxWidth={{ xs: "150px", md: "200px", lg: "250px" }}
          offsetX={{ xs: "-150px", md: "-100px", lg: "0" }}
          zIndex={0}
          rotationRange={40}
          scaleConfig={{ normal: 1, small: 0.6 }}
        />

        {/* Main content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: 10,
            position: "relative",
            zIndex: 1,
            width: "100%",
          }}
        >
          {/* Background glow effect */}
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              background: "radial-gradient(circle at center, rgba(0, 229, 255, 0.1) 0%, rgba(0, 0, 0, 0) 70%)",
              zIndex: -1,
            }}
          />

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(45deg, #ffffff 30%, #00e5ff 90%)",
              backgroundClip: "text",
              textFillColor: "transparent",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 2px rgba(0, 229, 255, 0.5)",
            }}
          >
            Welcome to DarkEdge
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              mb: 2,
              maxWidth: "600px",
            }}
          >
            DarkOrbit Unity Bot
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.5)",
              mb: 6,
              maxWidth: "600px",
              px: 2,
            }}
          >
            The only DarkOrbit bot that lets you choose between <Box component="span" sx={{ color: "#ce93d8", fontWeight: 600 }}>Memory</Box> and <Box component="span" sx={{ color: "#00e5ff", fontWeight: 600 }}>Packet</Box> bot mode — pick whichever suits your setup
          </Typography>

          {/* Discord Invite Banner */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "800px",
              mb: 6,
              background: "linear-gradient(90deg, rgba(13,15,25,0.7) 0%, rgba(13,15,25,0.9) 100%)",
              border: "1px solid rgba(0, 229, 255, 0.3)",
              borderRadius: "12px",
              overflow: "hidden",
              position: "relative",
              boxShadow: "0 0 20px rgba(0, 229, 255, 0.2)",
              "&:hover": {
                boxShadow: "0 0 30px rgba(0, 229, 255, 0.3)",
              },
              transition: "all 0.3s ease",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "1px",
                background: "linear-gradient(90deg, rgba(13,15,25,0.7) 0%, rgba(13,15,25,0.9) 100%)",
                zIndex: 1,
              },
            }}
          >
            {/* Circuit background */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.05,
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='smallGrid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='%2300e5ff' strokeWidth='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23smallGrid)'/%3E%3C/svg%3E\")",
              }}
            />

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center" }}>
              {/* Discord logo section */}
              <Box
                sx={{
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: "100%", md: "30%" },
                  borderRight: { xs: "none", md: "1px solid rgba(0, 229, 255, 0.2)" },
                  borderBottom: { xs: "1px solid rgba(0, 229, 255, 0.2)", md: "none" },
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  component={FaDiscord}
                  sx={{
                    fontSize: "4rem",
                    color: theme.palette.primary.main,
                    mb: 1,
                    filter: "drop-shadow(0 0 10px rgba(0, 229, 255, 0.7))",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main, // Changed from Discord brand color to theme color
                    textShadow: "0 0 5px rgba(0, 229, 255, 0.5)", // Changed shadow color
                  }}
                >
                  Discord Community
                </Typography>
              </Box>

              {/* Content section */}
              <Box
                sx={{
                  p: 4,
                  textAlign: "left",
                  width: { xs: "100%", md: "70%" },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, color: theme.palette.primary.main }}>
                  Join Our Discord Server
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, color: "rgba(255, 255, 255, 0.7)" }}>
                  Connect with other users, get support, and stay updated with the latest features and announcements.
                </Typography>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PeopleIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="body2">Active Community</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ForumIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="body2">24/7 Support</Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleExternalNavigation(DISCORD_INVITE_LINK, "Discord Server")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: "linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)", // Changed to match theme colors
                    boxShadow: "0 0 10px rgba(0, 229, 255, 0.5)", // Changed shadow color
                    "&:hover": {
                      boxShadow: "0 0 15px rgba(0, 229, 255, 0.8)", // Changed shadow color
                      background: "linear-gradient(45deg, #00c4dd 30%, #1a6fc7 90%)", // Slightly darker version for hover
                    },
                  }}
                >
                  Join Now
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  )
}

export default Home
